import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const inputStyle = { width: '100%', padding: '12px 16px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', letterSpacing: '0.02em', outline: 'none', transition: 'border-color 0.3s', marginBottom: '16px' };

export const LoginPage = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const result = await login(form);
    if (result.success) {
      navigate(result.data.role === 'admin' ? '/admin' : from, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--black)', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <Link to="/" style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--gold)', letterSpacing: '0.15em' }}>LUMIÈRE</Link>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 400, marginTop: '24px' }}>Welcome Back</h1>
          <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginTop: '8px' }}>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '40px' }}>
          {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', padding: '12px 16px', fontSize: '13px', color: '#ef4444', marginBottom: '20px' }}>{error}</div>}

          <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>EMAIL</label>
          <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required style={inputStyle} placeholder="your@email.com" />

          <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>PASSWORD</label>
          <div style={{ position: 'relative', marginBottom: '24px' }}>
            <input type={showPass ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required style={{ ...inputStyle, marginBottom: 0, paddingRight: '48px' }} placeholder="••••••••" />
            <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', color: 'var(--white-dim)' }}>
              {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <button type="submit" disabled={loading} style={{ width: '100%', background: 'var(--gold)', color: 'var(--black)', padding: '14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1, transition: 'background 0.3s' }}>
            {loading ? 'SIGNING IN...' : 'SIGN IN'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: 'var(--white-dim)' }}>
          New to Lumière?{' '}
          <Link to="/register" style={{ color: 'var(--gold)', borderBottom: '1px solid var(--gold)', paddingBottom: '1px' }}>Create account</Link>
        </p>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    const result = await register({ name: form.name, email: form.email, password: form.password });
    if (result.success) navigate('/');
    else setError(result.error);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--black)', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <Link to="/" style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--gold)', letterSpacing: '0.15em' }}>LUMIÈRE</Link>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 400, marginTop: '24px' }}>Create Account</h1>
          <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginTop: '8px' }}>Join our fragrance community</p>
        </div>

        <form onSubmit={handleSubmit} style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '40px' }}>
          {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', padding: '12px 16px', fontSize: '13px', color: '#ef4444', marginBottom: '20px' }}>{error}</div>}

          {[['FULL NAME', 'name', 'text', 'Your full name'], ['EMAIL', 'email', 'email', 'your@email.com']].map(([label, field, type, placeholder]) => (
            <div key={field}>
              <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>{label}</label>
              <input type={type} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} required style={inputStyle} placeholder={placeholder} />
            </div>
          ))}

          <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>PASSWORD</label>
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <input type={showPass ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required style={{ ...inputStyle, marginBottom: 0, paddingRight: '48px' }} placeholder="Min 6 characters" />
            <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', color: 'var(--white-dim)' }}>
              {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <label style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>CONFIRM PASSWORD</label>
          <input type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} required style={inputStyle} placeholder="Confirm password" />

          <button type="submit" disabled={loading} style={{ width: '100%', background: 'var(--gold)', color: 'var(--black)', padding: '14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1, marginTop: '8px' }}>
            {loading ? 'CREATING...' : 'CREATE ACCOUNT'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '13px', color: 'var(--white-dim)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--gold)', borderBottom: '1px solid var(--gold)', paddingBottom: '1px' }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
